import * as React from 'react';
import { Box, Image, Text } from 'wix-style-react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { Theme } from '@src/types';
import { DataHooks } from '@src/constants/data-hooks';
import { AppContext } from '@src/context/store';
import { getTheme } from '@src/context/selectors/app-settings';
import { getLiveSiteUrl, isDeclineAllEnabled } from '@src/context/selectors/ui-settings';

import bannerLight from './images/banner-light.svg';
import bannerDeclineAllLight from './images/banner-decline-all-light.svg';
import bannerDark from './images/banner-dark.svg';
import bannerDeclineAllDark from './images/banner-decline-all-dark.svg';
import popupLight from './images/popup-light.svg';
import popupDeclineAllLight from './images/popup-decline-all-light.svg';
import popupDark from './images/popup-dark.svg';
import popupDeclineAllDark from './images/popup-decline-all-dark.svg';
import * as css from './theme-preview.scss';

export type TThemeSkin = 'banner' | 'popup';
interface Props extends InjectedTranslateProps {
  skin: TThemeSkin;
}

const ThemePreviewComponent = ({ t, skin }: Props) => {
  const store = React.useContext(AppContext);

  const theme = getTheme(store);
  const liveSiteUrl = getLiveSiteUrl(store);
  const declineAllEnabled = isDeclineAllEnabled(store);

  const getBannerAttributes = () => {
    if (theme === Theme.light) {
      return declineAllEnabled
        ? [DataHooks.ThemeBannerDeclineAllPreviewImageLight, bannerDeclineAllLight]
        : [DataHooks.ThemeBannerPreviewImageLight, bannerLight];
    } else {
      return declineAllEnabled
        ? [DataHooks.ThemeBannerDeclineAllPreviewImageDark, bannerDeclineAllDark]
        : [DataHooks.ThemeBannerPreviewImageDark, bannerDark];
    }
  };

  const getPopupAttributes = () => {
    if (theme === Theme.light) {
      return declineAllEnabled
        ? [DataHooks.ThemePopupDeclineAllPreviewImageLight, popupDeclineAllLight]
        : [DataHooks.ThemePopupPreviewImageLight, popupLight];
    } else {
      return declineAllEnabled
        ? [DataHooks.ThemePopupDeclineAllPreviewImageDark, popupDeclineAllDark]
        : [DataHooks.ThemePopupPreviewImageDark, popupDark];
    }
  };

  const renderBannerTheme = () => {
    const attributes = getBannerAttributes();
    return <Image dataHook={attributes[0]} className={css.previewImageShadow} src={attributes[1]} />;
  };

  const renderPopupTheme = () => {
    const attributes = getPopupAttributes();
    return <Image dataHook={attributes[0]} className={css.previewImageShadow} src={attributes[1]} />;
  };

  return (
    <Box dataHook={DataHooks.ThemePreviewContainer} align="right">
      <Box className={css.previewContainer} padding={'30px'} width={372} height={384} direction="vertical">
        <Box>{skin === 'banner' ? renderBannerTheme() : renderPopupTheme()}</Box>
        <Box marginTop={'24px'} textAlign="center">
          <Text size="small">
            {t('text-and-display.preview-theme.info-part1')}
            <a href={liveSiteUrl} target="_blank">
              {t('text-and-display.preview-theme.info-link')}
            </a>
            {t('text-and-display.preview-theme.info-part2')}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export const ThemePreview = translate()(ThemePreviewComponent);
